<script>
  import { format } from 'svelte-i18n';
  import { Input, Label, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { generateUUID } from '@common/utils/strings';

  import AlignItems from './editors/align-items.svelte';
  import BackgroundImage from './editors/background-image.svelte';
  import Border from './editors/border.svelte';
  import BorderRadius from './editors/border-radius.svelte';
  import BoxShadow from './editors/box-shadow.svelte';
  import ClipPath from './editors/clip-path.svelte';
  import Color from './editors/color.svelte';
  import Display from './editors/display.svelte';
  import Float from './editors/float.svelte';
  import FontFamily from './editors/font-family.svelte';
  import JustifyContent from './editors/justify-content.svelte';
  import NumberRange from './editors/number-range.svelte';
  import TextAlign from './editors/text-align.svelte';
  import TextTransform from './editors/text-transform.svelte';

  /** @type {string} */
  export let label;

  /** @type {string} */
  export let name;

  /** @type {string | undefined} */
  export let property;

  /** @type {string} */
  export let value;

  /** @type {(name: string, value: string) => void} */
  export let onChange = (name, value) => {};

  let modifiedValue = value;

  const id = `variable-${generateUUID()}`;
  const numericCSSProperties = [
    'border-width',
    'font-size',
    'height',
    'line-height',
    'margin',
    'opacity',
    'padding',
    'width'
  ];
  const updateValue = (newValue) => {
    modifiedValue = newValue;
    onChange(name, newValue);
  };

  $: invalid = property && !CSS.supports(property, modifiedValue);
  $: modified = modifiedValue !== value;
  $: propertyLabel = label.replaceAll('-', ' ');
  $: {
    // Reset modified value when value changes from template
    modifiedValue = value;
  }
</script>

<div class="leaf-node mb-1">
  <Label for={name} class="small mb-0 text-capitalize text-nowrap {modified ? 'text-primary fw-medium' : ''}">
    {propertyLabel}
    {#if modified}
      <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions a11y-missing-attribute -->
      <a class="undo text-primary" on:click={() => updateValue(value)}>
        <Icon {id} name="undo-2" />
        <Tooltip target={id}>
          {$format('label.UNDO')}
        </Tooltip>
      </a>
    {/if}
  </Label>
  {#if property === 'align-items'}
    <AlignItems bind:value={modifiedValue} onChange={updateValue} />
  {:else if property === 'background-image'}
    <BackgroundImage value={modifiedValue} onChange={updateValue} />
  {:else if property === 'border'}
    <Border value={modifiedValue} onChange={updateValue} />
  {:else if property === 'border-radius'}
    <BorderRadius value={modifiedValue} onChange={updateValue} />
  {:else if property === 'box-shadow'}
    <BoxShadow value={modifiedValue} onChange={updateValue} />
  {:else if property === 'clip-path'}
    <ClipPath value={modifiedValue} onChange={updateValue} />
  {:else if property?.endsWith('color')}
    <Color id={name} {invalid} {name} value={modifiedValue} onChange={updateValue} />
  {:else if property === 'display'}
    <Display bind:value={modifiedValue} onChange={updateValue} />
  {:else if property?.endsWith('float')}
    <Float bind:value={modifiedValue} onChange={updateValue} />
  {:else if property?.endsWith('font-family')}
    <FontFamily bind:value={modifiedValue} onChange={updateValue} />
  {:else if property === 'justify-content'}
    <JustifyContent bind:value={modifiedValue} onChange={updateValue} />
  {:else if numericCSSProperties.includes(property)}
    <NumberRange {invalid} {property} value={modifiedValue} onChange={updateValue} />
  {:else if property === 'text-align'}
    <TextAlign bind:value={modifiedValue} onChange={updateValue} />
  {:else if property === 'text-transform'}
    <TextTransform bind:value={modifiedValue} onChange={updateValue} />
  {:else}
    <Input
      bsSize="sm"
      id={name}
      {invalid}
      {name}
      placeholder={name}
      bind:value={modifiedValue}
      on:input={(e) => updateValue(e.target.value)}
    />
  {/if}
</div>

<style lang="scss" src="./variable.scss"></style>
